'use client'

import { useLocale, useTranslations } from 'next-intl'
import type { ReactNode } from 'react'

import { Skeleton } from '@/components/ui/skeleton'
import { CryptoIcon } from '@/components/widget/crypto-icon'
import { ExchangeIcon } from '@/components/widget/exchange-icon'
import {
  type InstrumentChoiceData,
  ivPercent,
  type PositionChoiceData,
} from '@/features/order/choice-picker/lib/choice-data'
import {
  priceFormatOptions,
  SIGNED_PERCENT,
  signTextClass,
} from '@/features/order/choice-picker/lib/choice-format'
import type { UnifiedInstrumentKind } from '@/features/order/order-form/lib/instrument-filter'
import { useNumberFormat } from '@/hooks/use-number-format'
import { tradeVenueToIconKey } from '@/lib/proto/util'
import { cn } from '@/lib/utils'

// Instrument kind is a category, so it takes a cat-* tag (never a state color).
const KIND_TAG_CLASS: Record<UnifiedInstrumentKind, string> = {
  spot: 'border-cat-sky/50 text-cat-sky',
  perp: 'border-cat-violet/50 text-cat-violet',
  future: 'border-cat-blue/50 text-cat-blue',
  'option-call': 'border-cat-orange/50 text-cat-orange',
  'option-put': 'border-cat-orange/50 text-cat-orange',
}

const KIND_TAG_KEY = {
  spot: 'kind.spot',
  perp: 'kind.perp',
  future: 'kind.future',
  'option-call': 'kind.call',
  'option-put': 'kind.put',
} as const

const DAY_MS = 86_400_000

function Tag({ className, children }: { className: string; children: ReactNode }) {
  return (
    <span
      className={cn(
        'inline-flex h-4 shrink-0 items-center border px-1 text-[10px] leading-none font-medium uppercase',
        className
      )}
    >
      {children}
    </span>
  )
}

/** One "label value" pair in a card's detail line; a missing value pulses until it arrives. */
function Stat({
  label,
  value,
  className,
}: {
  label: string
  value: string | undefined
  className?: string
}) {
  return (
    <span className="inline-flex items-baseline gap-1 whitespace-nowrap">
      <span className="text-foreground/40">{label}</span>
      {value === undefined ? (
        <Skeleton className="inline-block h-2.5 w-8 self-center" />
      ) : (
        <span className={cn('text-foreground/80', className)}>{value}</span>
      )}
    </span>
  )
}

function useExpiryLabel() {
  const locale = useLocale()
  const t = useTranslations('orderForm.choicePicker')
  return (expiryMs: number, now: number) => {
    const date = new Date(expiryMs).toLocaleDateString(locale, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      timeZone: 'UTC',
    })
    const days = Math.max(0, Math.ceil((expiryMs - now) / DAY_MS))
    return `${date} · ${t('inDays', { days })}`
  }
}

/**
 * An instrument answer: symbol and kind up top, price and 24h move on the right, then the
 * detail that tells look-alikes apart — volume and funding for spot/perp, strike, expiry, mark,
 * IV and greeks for an option. `data` undefined: still loading, so values pulse.
 */
export function InstrumentChoiceCard({
  symbol,
  label,
  data,
  now,
}: {
  symbol: string
  label: string
  data: InstrumentChoiceData | undefined
  now: number
}) {
  const t = useTranslations('orderForm.choicePicker')
  const fmt = useNumberFormat()
  const expiryLabel = useExpiryLabel()
  const loading = data === undefined
  const kind = data?.kind
  const option = data?.option
  const base = data?.base ?? symbol.split(/[-_/]/)[0]
  const price = option ? option.markPrice : data?.lastPrice
  const showLabel = label !== symbol

  return (
    <span className="flex min-w-0 flex-1 items-start gap-2.5">
      <CryptoIcon symbol={base} iconTag={data?.iconTag} className="mt-0.5 size-6 shrink-0" />
      <span className="flex min-w-0 flex-1 flex-col gap-1">
        <span className="flex min-w-0 items-center gap-1.5">
          <span className="text-foreground min-w-0 truncate text-[13px] font-medium">{symbol}</span>
          {kind && <Tag className={KIND_TAG_CLASS[kind]}>{t(KIND_TAG_KEY[kind])}</Tag>}
          <span className="ml-auto shrink-0 pl-2 text-[13px] font-medium">
            {loading || (price === undefined && kind !== undefined) ? (
              <Skeleton className="h-3 w-14" />
            ) : (
              <span className="text-foreground">
                {fmt.number(price, priceFormatOptions(price, data?.tickSize))}
              </span>
            )}
          </span>
        </span>
        <span className="flex min-w-0 items-center gap-2 text-[11px]">
          <span className="text-foreground/50 min-w-0 truncate">
            {showLabel
              ? label
              : option?.strike !== undefined
                ? `${t('stat.strike')} ${fmt.number(option.strike, { maximumFractionDigits: 8 })}`
                : data?.quote
                  ? `${base} / ${data.quote}`
                  : loading
                    ? ''
                    : t('notListed')}
          </span>
          <span className="ml-auto shrink-0">
            {data?.changePercent24h !== undefined ? (
              <span className={signTextClass(data.changePercent24h)}>
                {fmt.number(data.changePercent24h, SIGNED_PERCENT)}%
              </span>
            ) : loading ? (
              <Skeleton className="h-2.5 w-10" />
            ) : null}
          </span>
        </span>
        {option ? (
          <>
            <span className="flex flex-wrap gap-x-3 gap-y-0.5 text-[11px]">
              {option.expiryMs !== undefined && (
                <span className="text-foreground/60">{expiryLabel(option.expiryMs, now)}</span>
              )}
              <Stat
                label={t('stat.iv')}
                value={
                  ivPercent(option.markIv) === undefined
                    ? undefined
                    : `${fmt.number(ivPercent(option.markIv), { maximumFractionDigits: 1 })}%`
                }
              />
            </span>
            <span className="flex flex-wrap gap-x-3 gap-y-0.5 text-[11px]">
              <Stat
                label="Δ"
                value={greek(fmt, option.delta, { maximumFractionDigits: 3 })}
                className={signTextClass(option.delta)}
              />
              <Stat label="Γ" value={greek(fmt, option.gamma, { maximumSignificantDigits: 2 })} />
              <Stat label="Θ" value={greek(fmt, option.theta, { maximumFractionDigits: 2 })} />
              <Stat label="V" value={greek(fmt, option.vega, { maximumFractionDigits: 2 })} />
            </span>
          </>
        ) : (
          kind && (
            <span className="flex flex-wrap gap-x-3 gap-y-0.5 text-[11px]">
              <Stat
                label={t('stat.volume')}
                value={
                  data?.volume24h === undefined
                    ? undefined
                    : fmt.number(data.volume24h, { notation: 'compact', maximumFractionDigits: 2 })
                }
              />
              {kind === 'perp' && (
                <Stat
                  label={t('stat.funding')}
                  value={
                    data?.fundingRate === undefined
                      ? undefined
                      : `${fmt.number(Number(data.fundingRate) * 100, {
                          minimumFractionDigits: 4,
                          maximumFractionDigits: 4,
                          signDisplay: 'exceptZero',
                        })}%`
                  }
                  className={signTextClass(data?.fundingRate)}
                />
              )}
            </span>
          )
        )}
      </span>
    </span>
  )
}

function greek(
  fmt: ReturnType<typeof useNumberFormat>,
  value: string | undefined,
  options: Intl.NumberFormatOptions
): string | undefined {
  return value === undefined ? undefined : fmt.number(value, options)
}

/**
 * A position answer: symbol, side and uPnL (in color, with %) up top, then size, entry → mark
 * and liquidation, and the account it is held on — enough to tell two BTC positions apart at a
 * glance. `data` undefined: no open position matches the suggestion.
 */
export function PositionChoiceCard({
  symbol,
  label,
  data,
}: {
  symbol: string
  label: string
  data: PositionChoiceData | undefined
}) {
  const t = useTranslations('orderForm.choicePicker')
  const fmt = useNumberFormat()
  const base = symbol.split(/[-_/]/)[0]

  if (!data) {
    return (
      <span className="flex min-w-0 flex-1 items-center gap-2.5">
        <CryptoIcon symbol={base} className="size-6 shrink-0" />
        <span className="flex min-w-0 flex-col">
          <span className="text-foreground truncate text-[13px] font-medium">{label}</span>
          <span className="text-foreground/50 text-[11px]">{t('noPosition')}</span>
        </span>
      </span>
    )
  }

  const pnl = data.unrealizedPnlUsd
  const price = (value: string | undefined) =>
    value === undefined ? undefined : fmt.number(value, priceFormatOptions(value))

  return (
    <span className="flex min-w-0 flex-1 items-start gap-2.5">
      <CryptoIcon symbol={base} className="mt-0.5 size-6 shrink-0" />
      <span className="flex min-w-0 flex-1 flex-col gap-1">
        <span className="flex min-w-0 items-center gap-1.5">
          <span className="text-foreground min-w-0 truncate text-[13px] font-medium">
            {data.symbol}
          </span>
          <Tag
            className={
              data.side === 'long'
                ? 'border-chart-mint/50 text-chart-mint'
                : 'border-chart-red/50 text-chart-red'
            }
          >
            {t(data.side === 'long' ? 'side.long' : 'side.short')}
          </Tag>
          <span className={cn('ml-auto shrink-0 pl-2 text-[13px] font-medium', signTextClass(pnl))}>
            {fmt.number(pnl, {
              style: 'currency',
              currency: 'USD',
              signDisplay: 'exceptZero',
              sensitive: true,
            })}
          </span>
        </span>
        <span className="flex min-w-0 items-center gap-2 text-[11px]">
          <span className="text-foreground/80 min-w-0 truncate">
            {fmt.number(data.quantity, { maximumFractionDigits: 8, sensitive: true })}
            {data.notionalUsd !== undefined && (
              <span className="text-foreground/50">
                {' ≈ '}
                {fmt.number(data.notionalUsd, {
                  style: 'currency',
                  currency: 'USD',
                  notation: data.notionalUsd >= 100_000 ? 'compact' : 'standard',
                  maximumFractionDigits: data.notionalUsd >= 100_000 ? 2 : 0,
                  sensitive: true,
                })}
              </span>
            )}
          </span>
          {data.pnlPercent !== undefined && (
            <span className={cn('ml-auto shrink-0', signTextClass(data.pnlPercent))}>
              {fmt.number(data.pnlPercent, SIGNED_PERCENT)}%
            </span>
          )}
        </span>
        <span className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px]">
          <Stat label={t('stat.entry')} value={price(data.entryPrice) ?? '—'} />
          <Stat label={t('stat.mark')} value={price(data.markPrice) ?? '—'} />
          {data.liquidationPrice && Number(data.liquidationPrice) > 0 && (
            <Stat label={t('stat.liq')} value={price(data.liquidationPrice)} />
          )}
          <span className="text-foreground/50 ml-auto inline-flex min-w-0 items-center gap-1">
            <ExchangeIcon
              exchange={tradeVenueToIconKey(data.tradeVenue)}
              className="size-3 shrink-0 rounded-full"
            />
            <span className="truncate">{data.accountName ?? data.accountId}</span>
          </span>
        </span>
        {label !== data.symbol && label !== symbol && (
          <span className="text-foreground/50 truncate text-[11px]">{label}</span>
        )}
      </span>
    </span>
  )
}
